import { useEffect, useMemo, useRef, useState } from 'react';
import { Briefcase, LayoutGrid, List, Map, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import Layout from '../../components/layout/layout';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';
import { getPublishedJobOffers } from '../../lib/offres';
import type {
  JobOffer,
  JobOfferContractType,
  JobOfferRemoteType,
} from '../../types/offres';

import {
  CONTRACT_TYPE_LABELS,
  REMOTE_TYPE_LABELS,
} from '../../types/offres';

import { OfferCard } from '../../components/offres/OfferCard';

// ---------------------------------------------------------------------------
// Leaflet map rendered imperatively (no react-leaflet dependency)
// ---------------------------------------------------------------------------
function OffersMap({ offers }: { offers: JobOffer[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Dynamically import leaflet so its CSS side-effects load on demand
    import('leaflet').then((L) => {
      // Fix broken default icon paths in bundlers
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (L.Icon.Default.prototype as any)._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl:
          'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl:
          'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl:
          'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });

      // Destroy previous instance if the component re-runs (StrictMode)
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }

      const map = L.map(containerRef.current!, {
        center: [46.6, 2.5],
        zoom: 6,
      });

      mapRef.current = map;

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(map);

      offers
        .filter((o) => o.latitude != null && o.longitude != null)
        .forEach((offer) => {
          const popup = L.popup().setContent(`
            <div style="font-size:13px;line-height:1.5;min-width:160px">
              <p style="font-weight:600;color:#0a0a0a;margin:0 0 2px">${offer.title}</p>
              ${offer.location_city ? `<p style="color:#737373;margin:0 0 6px">${offer.location_city}</p>` : ''}
              <a href="/jobs/${offer.id}" style="color:#d03484;font-weight:500;text-decoration:none">
                Voir l'offre &rarr;
              </a>
            </div>
          `);

          L.marker([offer.latitude as number, offer.longitude as number])
            .bindPopup(popup)
            .addTo(map);
        });
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers]);

  return (
    <>
      {/* Leaflet CSS injected inline to avoid needing a bundler import */}
      <link
        rel="stylesheet"
        href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
      />
      <div ref={containerRef} style={{ height: '100%', width: '100%' }} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export function JobsPage() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState<JobOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [contractType, setContractType] =
    useState<JobOfferContractType | 'all'>('all');

  const [remoteType, setRemoteType] =
    useState<JobOfferRemoteType | 'all'>('all');

  const [locationCity, setLocationCity] = useState('');

  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'map'>('list');

  useEffect(() => {
    let mounted = true;

    async function loadOffers() {
      try {
        setLoading(true);
        setError(null);

        const data = await getPublishedJobOffers({
          contractType,
          remoteType,
          locationCity,
        });

        if (mounted) setOffers(data);
      } catch (e) {
        console.error(e);
        if (mounted) setError('Impossible de charger les offres.');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadOffers();

    return () => {
      mounted = false;
    };
  }, [contractType, remoteType, locationCity]);

  const totalLabel = useMemo(() => {
    if (offers.length === 0) return 'Aucune offre disponible';
    if (offers.length === 1) return '1 offre disponible';
    return `${offers.length} offres disponibles`;
  }, [offers.length]);

  return (
    <Layout
      title="Offres emploi cadre Marseille, Aix, PACA · Réponse 48h | recruteurs.io"
      description="Offres d'emploi cadre Marseille, Aix-en-Provence, PACA, Poitiers. Réponse 100% sous 48h, Parcours Découverte avant signature, salaire annoncé. Process respectueux."
      keywords="offres emploi marseille, offres emploi aix-en-provence, offres cadre paca, jobs marseille, emploi cadre marseille, recrutement poitiers, offres emploi cadre, salaire annoncé"
    >
      <WebPageSchema
        page={{
          title:
            "Offres d'emploi cadre · Marseille, Aix, PACA, Poitiers | recruteurs.io",
          description:
            "Toutes les offres d'emploi cadre ouvertes avec recruteurs.io. Réponse 100 % sous 48h, Plateforme Parcours Découverte avant signature.",
          url: 'https://recruteurs.io/offres',
          lastReviewed: '2026-05-10',
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: "Offres d'emploi", url: 'https://recruteurs.io/offres' },
        ]}
      />

      <div className="min-h-screen bg-[#fbfaf8]">

        {/* HERO — pastel candidat + image fond, compact (filtres juste en dessous) */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#fff1f2] via-[#fef9c3] to-[#e0f2ff] border-b border-neutral-200">
          <div
            aria-hidden
            className="absolute inset-0 z-0 pointer-events-none select-none"
          >
            <img
              src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/offres-emplois-marseille.webp"
              alt=""
              className="w-full h-full object-cover opacity-15 md:opacity-20"
              fetchPriority="high"
              loading="eager"
              decoding="async"
            />
            <div className="absolute inset-0 bg-white/30" />
          </div>

          <div className="relative z-10 mx-auto max-w-5xl px-6 py-16 md:py-20 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2 text-xs md:text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6">
              <Briefcase className="h-4 w-4 text-[#d03484]" />
              Offres d'emploi · Marseille · Aix · PACA · Poitiers
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight leading-[1.1] pb-1">
              <span className="text-[#30475e]">Des opportunités </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                sélectionnées et qualifiées.
              </span>
            </h1>

            <p className="mt-5 max-w-2xl mx-auto text-base md:text-lg text-neutral-700 leading-relaxed">
              Pas un job board qui agrège tout et n'importe quoi. Chaque offre
              ici a été briefée, qualifiée et négociée avec un client que nous
              connaissons. Vous postulez, vous obtenez une vraie réponse sous 48 h.
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-neutral-600">
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-[#d03484]" />
                Réponse 100 % sous 48 h
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-[#d03484]" />
                Grille de rémunération transparente
              </span>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/candidat/decouvrir')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2 text-sm font-bold whitespace-nowrap text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Découvrir avant de signer
              </button>
              <button
                onClick={() => navigate('/candidat/pourquoi')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl ring-1 ring-neutral-300 bg-white px-5 py-2 text-sm font-semibold whitespace-nowrap text-[#30475e] hover:bg-white/80 transition-all"
              >
                Nos engagements candidats
              </button>
            </div>
          </div>
        </section>

        {/* FILTERS */}
        <section className="mx-auto max-w-6xl px-6 py-10">

          <div className="rounded-3xl bg-white p-6 ring-1 ring-neutral-200 shadow-sm">

            <div className="grid gap-4 md:grid-cols-3">

              <select
                value={contractType}
                onChange={(e) => setContractType(e.target.value as any)}
                className="rounded-xl border border-neutral-200 px-3 py-2 text-sm"
              >
                <option value="all">Tous les contrats</option>
                {Object.entries(CONTRACT_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>

              <select
                value={remoteType}
                onChange={(e) => setRemoteType(e.target.value as any)}
                className="rounded-xl border border-neutral-200 px-3 py-2 text-sm"
              >
                <option value="all">Tous les modes</option>
                {Object.entries(REMOTE_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>

              <input
                value={locationCity}
                onChange={(e) => setLocationCity(e.target.value)}
                placeholder="Ville"
                className="rounded-xl border border-neutral-200 px-3 py-2 text-sm"
              />
            </div>

            {/* SWITCH */}
            <div className="mt-5 flex items-center justify-between">

              <p className="text-sm text-neutral-600 font-medium">
                {loading ? 'Chargement...' : totalLabel}
              </p>

              <div className="flex items-center gap-1 rounded-xl bg-neutral-100 p-1">

                <button
                  onClick={() => setViewMode('grid')}
                  className={`flex items-center gap-2 rounded-lg px-3 py-1 text-sm ${
                    viewMode === 'grid'
                      ? 'bg-white shadow text-neutral-900'
                      : 'text-neutral-500'
                  }`}
                >
                  <LayoutGrid className="h-4 w-4" />
                  Cartes
                </button>

                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center gap-2 rounded-lg px-3 py-1 text-sm ${
                    viewMode === 'list'
                      ? 'bg-white shadow text-neutral-900'
                      : 'text-neutral-500'
                  }`}
                >
                  <List className="h-4 w-4" />
                  Liste
                </button>

                <button
                  onClick={() => setViewMode('map')}
                  className={`flex items-center gap-2 rounded-lg px-3 py-1 text-sm ${
                    viewMode === 'map'
                      ? 'bg-white shadow text-neutral-900'
                      : 'text-neutral-500'
                  }`}
                >
                  <Map className="h-4 w-4" />
                  Carte
                </button>

              </div>
            </div>
          </div>

          {/* LOADING */}
          {loading && (
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="rounded-3xl ring-1 ring-neutral-200 bg-white overflow-hidden animate-pulse"
                >
                  <div className="h-32 bg-neutral-100" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 bg-neutral-100 rounded w-3/4" />
                    <div className="h-3 bg-neutral-100 rounded w-full" />
                    <div className="h-3 bg-neutral-100 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ERROR */}
          {!loading && error && (
            <div className="mt-8 rounded-3xl bg-white ring-1 ring-neutral-200 shadow-sm p-10 text-center">
              <p className="text-neutral-700 font-medium mb-2">{error}</p>
              <p className="text-sm text-neutral-500">
                Réessayez dans quelques instants ou{' '}
                <button
                  onClick={() => navigate('/reservation')}
                  className="text-[#d03484] hover:underline font-semibold"
                >
                  contactez-nous
                </button>
                .
              </p>
            </div>
          )}

          {/* EMPTY */}
          {!loading && !error && offers.length === 0 && (
            <div className="mt-8 rounded-3xl bg-white ring-1 ring-neutral-200 shadow-sm p-10 md:p-14 text-center">
              <Briefcase className="w-10 h-10 text-neutral-300 mx-auto mb-4" />
              <p className="text-[#30475e] font-semibold text-lg mb-2">
                Aucune offre ne correspond à vos critères pour le moment.
              </p>
              <p className="text-sm text-neutral-500 mb-6">
                Élargissez vos filtres ou candidatez spontanément — nous gardons
                les profils qualifiés au chaud pour les prochaines missions.
              </p>
              <button
                onClick={() => navigate('/reservation')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2 text-sm font-bold whitespace-nowrap text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Candidature spontanée
              </button>
            </div>
          )}

          {/* GRID */}
          {!loading && !error && offers.length > 0 && viewMode === 'grid' && (
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              {offers.map((offer) => (
                <OfferCard key={offer.id} offer={offer} />
              ))}
            </div>
          )}

          {/* LIST */}
          {!loading && !error && offers.length > 0 && viewMode === 'list' && (
            <div className="mt-8 space-y-4">
              {offers.map((offer) => (
                <div
                  key={offer.id}
                  className="rounded-3xl bg-white p-2 shadow-sm hover:shadow-md transition"
                >
                  <OfferCard offer={offer} compact />
                </div>
              ))}
            </div>
          )}

          {/* MAP */}
          {!loading && !error && viewMode === 'map' && (
            <div
              className="mt-8 rounded-3xl overflow-hidden shadow-sm ring-1 ring-neutral-200"
              style={{ height: '600px' }}
            >
              <OffersMap offers={offers} />
            </div>
          )}

        </section>
      </div>
    </Layout>
  );
}
